import { formatTime12h, parseTimeParts, toHHmm } from '../lib/time12h';

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = [0, 15, 30, 45];

/** 12-hour time picker that keeps value as HH:mm for the API. */
export function Time12hInput({
  label,
  value,
  onChange,
  allowAnyMinute = false,
  optional = false,
}: {
  label?: string;
  value: string;
  onChange: (hhmm: string) => void;
  /** When true, include the current minute even if not on a 15-min step. */
  allowAnyMinute?: boolean;
  /** Allow clearing the value (for bulk “only fill what you change”). */
  optional?: boolean;
}) {
  const empty = optional && !value;
  const parts = empty ? { hour12: 9, minute: 0, ampm: 'AM' as const } : parseTimeParts(value);
  const minuteOptions = allowAnyMinute && !empty && !MINUTES.includes(parts.minute)
    ? [...MINUTES, parts.minute].sort((a, b) => a - b)
    : MINUTES;

  const selectClass =
    'border border-line rounded-radius-sm bg-surface text-ink text-sm px-2 py-2.5 min-h-[44px] touch-manipulation';

  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs font-medium text-ink-soft">{label}</label>}
      <div className="flex items-center gap-1.5 flex-wrap">
        {optional && (
          <button
            type="button"
            className="text-xs text-sand-strong hover:underline shrink-0"
            onClick={() => onChange('')}
          >
            Clear
          </button>
        )}
        <select
          aria-label={label ? `${label} hour` : 'Hour'}
          className={selectClass}
          value={empty ? '' : parts.hour12}
          onChange={e => {
            const hour12 = Number(e.target.value);
            if (!hour12) {
              onChange('');
              return;
            }
            onChange(toHHmm(hour12, parts.minute, parts.ampm));
          }}
        >
          {optional && <option value="">—</option>}
          {HOURS.map(h => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <span className="text-ink-faint font-medium">:</span>
        <select
          aria-label={label ? `${label} minutes` : 'Minutes'}
          className={selectClass}
          value={empty ? '' : parts.minute}
          disabled={empty}
          onChange={e => onChange(toHHmm(parts.hour12, Number(e.target.value), parts.ampm))}
        >
          {optional && empty && <option value="">—</option>}
          {minuteOptions.map(m => (
            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
          ))}
        </select>
        <select
          aria-label={label ? `${label} AM or PM` : 'AM or PM'}
          className={selectClass}
          value={empty ? '' : parts.ampm}
          disabled={empty}
          onChange={e => onChange(toHHmm(parts.hour12, parts.minute, e.target.value as 'AM' | 'PM'))}
        >
          {optional && empty && <option value="">—</option>}
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
      {value ? (
        <span className="sr-only">{formatTime12h(value)}</span>
      ) : null}
    </div>
  );
}
